import clsxm from '@/lib/clsxm';
import { CheckIcon } from '@heroicons/react/outline';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import useLocale from 'next-translate/useTranslation';
import React, { useRef, useState } from 'react';

function TiltCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [6, -6]), {
    stiffness: 200,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), {
    stiffness: 200,
    damping: 20,
  });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformPerspective: 800 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const editingSteps = [
  {
    id: 'walls',
    textKey: 'multi-turn-step-walls',
    icon: '🎨',
  },
  {
    id: 'bookshelf',
    textKey: 'multi-turn-step-bookshelf',
    icon: '📚',
  },
  {
    id: 'sofa',
    textKey: 'multi-turn-step-sofa',
    icon: '🛋️',
  },
  {
    id: 'rug',
    textKey: 'multi-turn-step-rug',
    icon: '🪶',
  },
  {
    id: 'coffee-table',
    textKey: 'multi-turn-step-coffee-table',
    icon: '🪑',
  },
];

// prop is from dashboard
export function MultiTurnEditingSection({
  fromDashboard = false,
}: {
  fromDashboard?: boolean;
}) {
  const { t } = useLocale('ai-interior');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handleVideoIntersection = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && videoRef.current) {
      videoRef.current.play().catch(() => {});
      setIsVideoPlaying(true);
    }
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(handleVideoIntersection, {
      threshold: 0.5,
    });

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, []);

  // Auto-advance steps when video is playing
  React.useEffect(() => {
    if (!isVideoPlaying) return;

    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= editingSteps.length - 1) {
          return 0; // Loop back to start
        }
        return prev + 1;
      });
    }, 4000); // Advance every 3 seconds

    return () => clearInterval(interval);
  }, [isVideoPlaying]);

  return (
    <section
      className={clsxm(
        fromDashboard ? 'pb-16 sm:pb-24' : 'py-24 sm:py-32',
        'relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 ',
      )}
    >
      {/* Background decoration */}
      <div className='absolute inset-0 -z-10'>
        <div className='absolute inset-y-0 left-1/2 -z-10 w-full -translate-x-1/2'>
          <svg className='absolute inset-0 h-full w-full' aria-hidden='true'>
            <defs>
              <pattern
                id='multi-edit-pattern'
                width={200}
                height={200}
                x='50%'
                y={0}
                patternUnits='userSpaceOnUse'
              >
                <path
                  d='M100 200V.5M.5 100H200'
                  fill='none'
                  className='stroke-gray-100 dark:stroke-slate-800'
                />
              </pattern>
            </defs>
            <rect
              width='100%'
              height='100%'
              strokeWidth={0}
              fill='url(#multi-edit-pattern)'
            />
          </svg>
        </div>
      </div>

      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        {!fromDashboard && (
          <div className='mx-auto max-w-2xl text-center'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className='inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-1.5 text-sm font-medium text-white shadow-lg shadow-indigo-500/25'>
                <span className='mr-1.5'>✨</span> {t('multi-turn-new-feature')}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className='mt-8 text-3xl font-black tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'
            >
              {t('multi-turn-heading')}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className='mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300'
            >
              {t('multi-turn-description')}
            </motion.p>
          </div>
        )}

        {fromDashboard && (
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className='text-xl text-center mb-4 font-bold tracking-tight bg-gradient-to-r from-purple-600 via-red-500 to-primary-600 bg-clip-text text-transparent sm:text-4xl lg:text-3xl'
          >
            {t('multi-turn-heading')}
          </motion.h2>
        )}

        <div
          className={clsxm(
            ' grid gap-8  lg:gap-16',
            fromDashboard ? 'grid-cols-1' : 'lg:grid-cols-3 mt-16',
          )}
        >
          {/* Video Section ocupies 60% of the width */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            viewport={{ once: true }}
            className='relative col-span-2 overflow-hidden rounded-2xl bg-gray-900 shadow-2xl'
          >
            <div className='absolute inset-0 bg-gradient-to-tr from-indigo-500/20 to-purple-500/20' />
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              className='h-full w-full object-cover'
              aria-label='Multi-turn image editing demonstration showing room decoration'
            >
              <source
                src='https://storage.googleapis.com/gweb-uniblog-publish-prod/original_videos/ImageEditingGemini_Inline_XZuiDzE.mp4'
                type='video/mp4'
              />
              Your browser doesn't support embedded videos.
            </video>

            {/* Play indicator */}
            <div className='absolute bottom-4 left-4 flex items-center space-x-2 rounded-full bg-black/50 px-3 py-1.5 backdrop-blur-sm'>
              <div className='h-2 w-2 animate-pulse rounded-full bg-green-400' />
              <span className='text-xs font-medium text-white'>
                {t('multi-turn-live-demo')}
              </span>
            </div>
          </motion.div>

          {/* Steps Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            viewport={{ once: true }}
            className={clsxm(
              'flex flex-col justify-center col-span-1',
              fromDashboard ? 'hidden' : '',
            )}
          >
            <div className='mb-8'>
              <h3 className='text-2xl font-bold text-gray-900 dark:text-white'>
                {t('multi-turn-section-heading')}
              </h3>
              <p className='mt-2 text-gray-600 dark:text-gray-400'>
                {t('multi-turn-section-description')}
              </p>
            </div>

            <div className='space-y-4'>
              {editingSteps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.4 + index * 0.1,
                  }}
                  viewport={{ once: true }}
                  className={`group relative flex items-center rounded-xl border-2 p-4 transition-all duration-300 ${
                    index <= currentStep
                      ? 'border-indigo-500 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30'
                      : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-slate-600'
                  }`}
                >
                  {/* Step number or check */}
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                      index <= currentStep
                        ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-400'
                    }`}
                  >
                    {index < currentStep ? (
                      <CheckIcon className='h-5 w-5' />
                    ) : (
                      <span className='text-lg'>{step.icon}</span>
                    )}
                  </div>

                  {/* Step content */}
                  <div className='ml-4 flex-1'>
                    <p
                      className={`text-base font-medium transition-colors duration-300 ${
                        index <= currentStep
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {t(step.textKey)}
                    </p>
                    {index === currentStep && (
                      <div className='mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700'>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: '100%' }}
                          transition={{ duration: 3, ease: 'linear' }}
                          className='h-full bg-gradient-to-r from-indigo-500 to-purple-600'
                        />
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  {index < currentStep && (
                    <span className='ml-2 text-sm font-medium text-green-600 dark:text-green-400'>
                      {t('multi-turn-status-complete')}
                    </span>
                  )}
                  {index === currentStep && (
                    <span className='ml-2 animate-pulse text-sm font-medium text-indigo-600 dark:text-indigo-400'>
                      {t('multi-turn-status-in-progress')}
                    </span>
                  )}
                </motion.div>
              ))}
            </div>
            {/* CTA Button */}
            {!fromDashboard && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                viewport={{ once: true }}
                className='mt-8'
              >
                <a
                  href='/login?preset=ai-interior-designer'
                  className='group inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-indigo-500/20 transition-all duration-300 hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl hover:shadow-indigo-500/30 hover:scale-[1.03] active:scale-[0.98]'
                >
                  {t('multi-turn-cta')}
                  <svg
                    className='ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                </a>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Feature highlights */}
        {!fromDashboard && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            viewport={{ once: true }}
            className='mt-20 grid gap-8 sm:grid-cols-3'
          >
            {[
              {
                titleKey: 'multi-turn-feature-preserves-title',
                descriptionKey: 'multi-turn-feature-preserves-desc',
                icon: '🎯',
              },
              {
                titleKey: 'multi-turn-feature-iterations-title',
                descriptionKey: 'multi-turn-feature-iterations-desc',
                icon: '♾️',
              },
              {
                titleKey: 'multi-turn-feature-precision-title',
                descriptionKey: 'multi-turn-feature-precision-desc',
                icon: '🤖',
              },
            ].map((feature, index) => (
              <TiltCard
                key={index}
                className='rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm transition-shadow duration-300 hover:shadow-xl dark:border-slate-700 dark:bg-slate-800'
              >
                <div className='mb-4 text-5xl'>{feature.icon}</div>
                <h3 className='text-lg font-bold text-gray-900 dark:text-white'>
                  {t(feature.titleKey)}
                </h3>
                <p className='mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400'>
                  {t(feature.descriptionKey)}
                </p>
              </TiltCard>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
